package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.FirebaseStorageService
import co.uk.sacursoftware.ssinventory.util.StorageArea
import co.uk.sacursoftware.ssinventory.util.rememberPlatformFilePicker
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddEditFileScreen(
    fileItem: FileItem?,
    presetFolderId: String?,
    onBack: () -> Unit,
    onSaved: () -> Unit
) {
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    val storageService = remember { FirebaseStorageService() }

    var title by remember { mutableStateOf(fileItem?.title ?: "") }
    var description by remember { mutableStateOf(fileItem?.description ?: "") }
    var folders by remember { mutableStateOf<List<FileFolder>>(emptyList()) }
    var selectedFolderId by remember { mutableStateOf(fileItem?.folderId ?: presetFolderId.orEmpty()) }
    var folderDropdownExpanded by remember { mutableStateOf(false) }
    val files = remember { mutableStateListOf<String>().apply { addAll(fileItem?.files ?: emptyList()) } }
    var isUploading by remember { mutableStateOf(false) }
    var isSaving by remember { mutableStateOf(false) }
    var pendingFileIndex by remember { mutableStateOf<Int?>(null) }

    LaunchedEffect(Unit) {
        repository.getFileFolders().onSuccess { list ->
            folders = list
            if (selectedFolderId.isEmpty() && list.isNotEmpty()) {
                selectedFolderId = list.first().id
            }
        }
    }

    val filePicker = rememberPlatformFilePicker { bytes, name ->
        scope.launch {
            isUploading = true
            try {
                val url = storageService.uploadFile(bytes, StorageArea.FILES, name)
                files.add(url)
                if (title.isBlank()) {
                    title = name.substringBeforeLast('.').ifBlank { name }
                }
            } catch (e: Exception) {
                snackbarHostState.showSnackbar(e.message ?: "Upload failed")
            }
            isUploading = false
        }
    }

    val selectedFolder = folders.find { it.id == selectedFolderId }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { Text(if (fileItem == null) "Add file" else "Edit file") },
                navigationIcon = {
                    IconButton(onClick = onBack) { BackArrowIcon() }
                },
                actions = {
                    TextButton(
                        onClick = {
                            if (title.isBlank() || selectedFolderId.isBlank() || files.isEmpty()) {
                                scope.launch { snackbarHostState.showSnackbar("Title, folder and at least one file are required") }
                                return@TextButton
                            }
                            scope.launch {
                                isSaving = true
                                val req = SaveFileItemRequest(
                                    id = fileItem?.id,
                                    title = title.trim(),
                                    description = description.ifBlank { null },
                                    folderId = selectedFolderId,
                                    files = files.toList()
                                )
                                repository.saveFileItem(req).onSuccess { onSaved() }
                                    .onFailure { snackbarHostState.showSnackbar(it.message ?: "Save failed") }
                                isSaving = false
                            }
                        },
                        enabled = !isSaving && !isUploading
                    ) {
                        Text("SAVE", fontWeight = FontWeight.Bold)
                    }
                }
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier.padding(padding).fillMaxSize().verticalScroll(rememberScrollState()).padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            OutlinedTextField(
                value = title,
                onValueChange = { title = it },
                label = { Text("Title *") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                shape = MaterialTheme.shapes.small
            )

            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text("Folder *", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold)
                ExposedDropdownMenuBox(
                    expanded = folderDropdownExpanded,
                    onExpandedChange = { folderDropdownExpanded = !folderDropdownExpanded }
                ) {
                    OutlinedTextField(
                        value = selectedFolder?.let {
                            "${it.folderName}${it.subFolderName?.let { sub -> " -> $sub" } ?: ""}"
                        } ?: "Select folder",
                        onValueChange = {},
                        readOnly = true,
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = folderDropdownExpanded) },
                        modifier = Modifier.fillMaxWidth().menuAnchor(),
                        shape = MaterialTheme.shapes.small
                    )
                    ExposedDropdownMenu(
                        expanded = folderDropdownExpanded,
                        onDismissRequest = { folderDropdownExpanded = false }
                    ) {
                        folders.forEach { folder ->
                            DropdownMenuItem(
                                text = { Text("${folder.folderName}${folder.subFolderName?.let { " -> $it" } ?: ""}") },
                                onClick = {
                                    selectedFolderId = folder.id
                                    folderDropdownExpanded = false
                                }
                            )
                        }
                    }
                }
            }

            OutlinedTextField(
                value = description,
                onValueChange = { description = it },
                label = { Text("Notes (optional)") },
                modifier = Modifier.fillMaxWidth().height(120.dp),
                shape = MaterialTheme.shapes.small
            )

            Text("Attachments (${files.size})", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
            OutlinedButton(
                onClick = { filePicker.pickFile() },
                modifier = Modifier.fillMaxWidth(),
                enabled = !isUploading
            ) {
                Text(if (isUploading) "Uploading..." else "Choose file")
            }

            files.forEachIndexed { index, url ->
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    shape = MaterialTheme.shapes.small,
                    border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(8.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(displayFileName(url), modifier = Modifier.weight(1f), style = MaterialTheme.typography.bodyMedium, maxLines = 2)
                        TextButton(onClick = { pendingFileIndex = index }) {
                            Text("Remove", color = MaterialTheme.colorScheme.error)
                        }
                    }
                }
            }
        }
    }

    pendingFileIndex?.let { index ->
        ConfirmDeleteDialog(
            title = "Remove attachment?",
            message = "Remove ${displayFileName(files.getOrNull(index).orEmpty())}?",
            confirmLabel = "Remove",
            onConfirm = {
                val url = files.getOrNull(index)
                if (index in files.indices) files.removeAt(index)
                pendingFileIndex = null
                if (url != null) {
                    scope.launch { storageService.deleteOrderImage(url) }
                }
            },
            onDismiss = { pendingFileIndex = null }
        )
    }
}
